import { Button, Icon, Tooltip } from '@lightspeed/design-system-react'
import { ApiRetailerPaymentType } from '@vend/types'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { T, useT } from '@transifex/react'
import { useLocalizedPaymentTypeName } from '@vend/react-utilities'
import { useAppSelector } from '../../../state/hooks'
import { selectOutstandingBalance } from '../../../state/slices/currentSale/currentSaleSlice'
import { useWebRegisterStatusManager } from '../../../ng-compat/hooks'
import {
  EVENTS,
  FEATURE_INTEGRATED_PAYMENT_OVERPAYMENT_FIX,
} from '../../../constants'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { usePaymentRecalculationLock } from './PaymentRecalculationLockContext'

export interface Props {
  paymentType?: ApiRetailerPaymentType
  pay: (payment: ApiRetailerPaymentType) => void
}

export const PayPalRefundButton: FunctionComponent<Props> = ({
  paymentType,
  pay,
}) => {
  const localizedPaymentTypeName = useLocalizedPaymentTypeName()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const { arePaymentActionsLocked } = usePaymentRecalculationLock()
  const overpaymentFixEnabled = getFeatureFromCache(
    FEATURE_INTEGRATED_PAYMENT_OVERPAYMENT_FIX
  )
  const [isOffline, setIsOffline] = useState<boolean>(
    !webRegisterStatusManager.isOnline()
  )
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const t = useT()

  const handlePay = useCallback(() => {
    if (!paymentType) {
      return
    }

    pay(paymentType)
  }, [paymentType, pay])

  useEffect(() => {
    webRegisterStatusManager.on(EVENTS.transitionOnline, () =>
      setIsOffline(false)
    )
    webRegisterStatusManager.on(EVENTS.transitionOffline, () =>
      setIsOffline(true)
    )

    return () => {
      webRegisterStatusManager.off(EVENTS.transitionOnline, () =>
        setIsOffline(false)
      )
      webRegisterStatusManager.off(EVENTS.transitionOffline, () =>
        setIsOffline(true)
      )
    }
  }, [setIsOffline, webRegisterStatusManager])

  if (!paymentType) {
    return
  }

  return (
    <Tooltip
      className="wr-tooltip"
      direction="center"
      message={
        <T
          _str="You need to be connected to the internet to take payments using this payment type."
          _context="tooltip message for paypal payments when offline"
          lineBreak={<br />}
        />
      }
      header={t('You can’t use this payment type while offline.', {
        _context: 'tooltip message for paypal payments when offline',
      })}
      canBeShown={isOffline}
      icon="info-circle"
    >
      <Button
        onClick={handlePay}
        variant={outstandingBalance < 0 ? 'no' : 'go'}
        className="vd-btn vd-btn--jumbo vd-mt5"
        disabled={
          (overpaymentFixEnabled && arePaymentActionsLocked) || isOffline
        }
        data-track={`payment-type-${paymentType.name}`}
      >
        <Icon icon="credit-card" />
        <span className="vd-ml2">
          {localizedPaymentTypeName(paymentType.name)}
        </span>
      </Button>
    </Tooltip>
  )
}
